import React, { useState, useEffect, useCallback, useRef } from 'react';
import { T, API_BASE } from '../constants';

// ANYANWỤ Monitor — v3.0 GRADE-SIZED. RTH sibling of OṢUPA. Polls
// /strategies/api/strategies/anyanwu/decisions/ every 20s. Every HOLD morning fires a
// LONG (size A/B=1.0x, C=0.5x) and — when grade_S is A/B — a SHORT (1.0x; C=skip).
// Causal grades: grade_L = -z(osupa_score) - z(on_ret); grade_S = z(on_ret)+z(pct)+z(mom5).
// Entry 09:30 ET, exit 16:00 ET, -125bp backstop. status ∈ {OPEN, TIME_1600, STOP_-125, SKIP}.
// certs LONG 4FF3D5E0 / SHORT 11158C7C. Rows come in TRADE+EXIT pairs.

const REFRESH_MS = 20000;
const ANYANWU_COLOR = '#f59e0b';   // amber ("anyanwu" = sun → RTH session)

const STATUS_COLORS = {
  OPEN: '#3b82f6', TIME_1600: T.bull, 'STOP_-125': T.bear, STOP: T.bear, SKIP: T.textMuted, EXIT: '#3b82f6',
};
const decisionColor = (d) =>
  (d === 'TRADE' ? ANYANWU_COLOR : d === 'EXIT' ? '#3b82f6' : T.textMuted);
const dirColor = (d) => (d === 'LONG' ? T.bull : d === 'SHORT' ? T.bear : T.textMuted);
const tierColor = (t) => (t === 'A' ? T.bull : t === 'B' ? '#3b82f6' : t === 'C' ? T.gold : T.textMuted);

function fmtET(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    const time = d.toLocaleTimeString('en-GB', { timeZone: 'America/New_York', hour12: false,
      hour: '2-digit', minute: '2-digit', second: '2-digit' });
    return `${date} ${time}`;
  } catch { return iso; }
}
const todayETStr = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
const num = (v, d = 2) => (v == null ? '—' : Number(v).toFixed(d));

function Pill({ value, color, mono = false }) {
  if (value == null || value === '') return <span style={{ color: T.textMuted }}>—</span>;
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10,
      fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
      background: `${color || T.border}22`, border: `1px solid ${color || T.border}66`,
      color: color || T.text, fontFamily: mono ? 'Roboto Mono, monospace' : 'inherit' }}>{String(value)}</span>
  );
}
function Stat({ label, value, color }) {
  return (
    <div style={{ flex: '1 1 110px', padding: '12px 14px', borderRadius: 6,
      background: 'rgba(255,255,255,0.02)', border: `1px solid ${T.border}` }}>
      <div style={{ fontSize: 10, color: T.textMuted, fontWeight: 700, letterSpacing: '0.08em',
        textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 22, color: color || T.text, fontFamily: 'Roboto Mono, monospace', marginTop: 4 }}>{value}</div>
    </div>
  );
}

export default function AnyanwuMonitor() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [actionFilter, setActionFilter] = useState('');
  const [limit, setLimit] = useState(300);
  const [since, setSince] = useState('');
  const timerRef = useRef(null);

  const fetchRows = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      let url = `${API_BASE}/strategies/api/strategies/anyanwu/decisions/?limit=${limit}`;
      if (actionFilter) url += `&action=${actionFilter}`;
      if (since) url += `&since=${since}`;
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setRows(j.decisions || []);
      setLastFetched(new Date());
    } catch (e) { setError(e.message || String(e)); }
    finally { setLoading(false); }
  }, [actionFilter, limit, since]);

  useEffect(() => {
    fetchRows();
    timerRef.current = setInterval(fetchRows, REFRESH_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [fetchRows]);

  // P&L (sized) lives on resolved rows; sum EXIT rows only to avoid double counting TRADE+EXIT.
  const c = rows.reduce((a, r) => {
    if (r.action === 'TAKE') {
      a.takes += 1; if (!r.exit_time) a.open += 1;
      if (r.direction === 'LONG') a.long += 1; else if (r.direction === 'SHORT') a.short += 1;
    } else if (r.action === 'EXIT') {
      a.exits += 1;
      if (r.pnl_bp != null) a.net += r.pnl_bp;
      if (r.pnl_sized_bp != null) a.netSized += r.pnl_sized_bp;
    } else { a.skips += 1; }
    return a;
  }, { takes: 0, long: 0, short: 0, open: 0, exits: 0, skips: 0, net: 0, netSized: 0 });

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', background: T.bg }}>
    <div style={{ padding: 'clamp(12px, 3vw, 20px) clamp(10px, 4vw, 28px)', maxWidth: 1900, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap',
        paddingBottom: 14, borderBottom: `1px solid ${T.border}`, marginBottom: 18 }}>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: T.text }}>ANYANWỤ — RTH Grade-Sized (NQ) · v3.0</h1>
        <span style={{ fontSize: 11, color: T.textMuted }}>
          Causal grade + sizing · LONG every HOLD morning (A/B=1.0x, C=0.5x) · SHORT A/B=1.0x (C skip) ·
          entry 09:30 → exit 16:00 ET · −125bp backstop · cert LONG 4FF3D5E0 / SHORT 11158C7C
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center',
          flexWrap: 'wrap', justifyContent: 'flex-end', flex: '1 1 360px' }}>
          <label style={{ fontSize: 11, color: T.textMuted }}>Since</label>
          <input type="date" value={since} max={todayETStr()} onChange={e => setSince(e.target.value)}
            style={{ padding: '4px 8px', fontSize: 12, borderRadius: 4, border: `1px solid ${T.border}`,
              background: T.bg, color: T.text, fontFamily: 'Roboto Mono, monospace' }} />
          <button onClick={() => setSince('')} style={{ padding: '4px 8px', fontSize: 10, fontWeight: 700,
            letterSpacing: '0.05em', textTransform: 'uppercase', borderRadius: 4, cursor: 'pointer',
            border: `1px solid ${T.border}`, background: 'transparent', color: T.textMuted }}>Clear</button>
          <select value={actionFilter} onChange={e => setActionFilter(e.target.value)}
            style={{ padding: '4px 8px', fontSize: 11, borderRadius: 4, border: `1px solid ${T.border}`, background: T.bg, color: T.text }}>
            <option value="">All actions</option><option value="TAKE">TAKE</option>
            <option value="EXIT">EXIT</option><option value="SKIP">SKIP</option>
          </select>
          <select value={limit} onChange={e => setLimit(parseInt(e.target.value, 10))}
            style={{ padding: '4px 8px', fontSize: 11, borderRadius: 4, border: `1px solid ${T.border}`, background: T.bg, color: T.text }}>
            <option value={200}>200 rows</option><option value={300}>300 rows</option>
            <option value={500}>500 rows</option>
          </select>
          <button onClick={fetchRows} disabled={loading} style={{ padding: '5px 12px', fontSize: 11, fontWeight: 700,
            letterSpacing: '0.07em', textTransform: 'uppercase', borderRadius: 4, cursor: loading ? 'wait' : 'pointer',
            border: 'none', background: `${ANYANWU_COLOR}2e`, color: ANYANWU_COLOR }}>{loading ? 'Loading…' : 'Refresh'}</button>
        </div>
      </div>

      {error && (
        <div style={{ padding: 10, marginBottom: 12, borderRadius: 4, background: 'rgba(239,68,68,0.1)',
          border: `1px solid ${T.bear}`, color: T.bear, fontSize: 12 }}>Error: {error}</div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <Stat label="Rows" value={rows.length} color={T.text} />
        <Stat label="Trades" value={c.takes} color={ANYANWU_COLOR} />
        <Stat label="Long / Short" value={`${c.long} / ${c.short}`} color={T.bull} />
        <Stat label="Open" value={c.open} color={STATUS_COLORS.OPEN} />
        <Stat label="Exits" value={c.exits} color={T.bull} />
        <Stat label="C-skip" value={c.skips} color={T.textMuted} />
        <Stat label="Net bp (1x)" value={num(c.net, 0)} color={c.net >= 0 ? T.bull : T.bear} />
        <Stat label="Net bp (sized)" value={num(c.netSized, 0)} color={c.netSized >= 0 ? T.bull : T.bear} />
      </div>

      <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 8, fontFamily: 'Roboto Mono, monospace' }}>
        RTH grade-sized log · {rows.length} rows · newest first · sized return ≈ {num(c.netSized / 100, 1)}%
        {lastFetched && ` · updated ${lastFetched.toLocaleTimeString('en-GB', { hour12: false })} ET`}
        <span style={{ marginLeft: 10 }}>· exits: <span style={{ color: T.bull }}>16:00</span> ·{' '}
          <span style={{ color: T.bear }}>−125bp</span></span>
      </div>

      <div style={{ overflowX: 'auto', border: `1px solid ${T.border}`, borderRadius: 6, background: 'rgba(255,255,255,0.01)' }}>
        <table style={{ width: '100%', minWidth: 1620, borderCollapse: 'collapse', fontSize: 11, fontFamily: 'Roboto Mono, monospace' }}>
          <thead>
            <tr style={{ background: 'rgba(255,255,255,0.04)', textAlign: 'left', color: T.textMuted,
              fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              {['Time (ET)', 'Session', 'Decision', 'Dir', 'Tier', 'Size', 'on_ret bp',
                'Entry $', 'Stop $', 'Status', 'Exit (ET)', 'PnL bp', 'Sized bp', 'Reason'].map((h, i) => (
                <th key={i} style={{ padding: '8px 10px', textAlign: i >= 5 && i <= 12 && i !== 9 && i !== 10 ? 'right' : 'left' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={14} style={{ padding: '20px 14px', color: T.textMuted, textAlign: 'center', fontStyle: 'italic' }}>
                No ANYANWỤ rows yet. Every HOLD morning fires a sized LONG (and an A/B SHORT),
                entering 09:30 ET, exiting 16:00 ET. Auto-refresh {REFRESH_MS / 1000}s.
              </td></tr>
            )}
            {rows.map(r => {
              const st = (r.status || (r.action === 'TAKE' ? 'OPEN' : r.action));
              const stColor = STATUS_COLORS[st] || T.textMuted;
              const open = r.action === 'TAKE' && !r.exit_time;
              const isExit = r.action === 'EXIT';
              const tier = r.anyanwu_tier || r.grade_tier || r.tier;
              return (
                <tr key={r.id} style={{ borderTop: `1px solid ${T.border}`,
                  background: open ? 'rgba(59,130,246,0.06)' : isExit ? 'rgba(245,158,11,0.04)' : 'transparent' }}>
                  <td style={{ padding: '6px 10px', color: T.text }}>{fmtET(r.signal_ts)}</td>
                  <td style={{ padding: '6px 10px', color: T.textMuted }}>{r.session_date || '—'}</td>
                  <td style={{ padding: '6px 10px' }}><Pill value={r.decision} color={decisionColor(r.decision)} /></td>
                  <td style={{ padding: '6px 10px' }}><Pill value={r.direction} color={dirColor(r.direction)} mono /></td>
                  <td style={{ padding: '6px 10px' }}><Pill value={tier} color={tierColor(tier)} mono /></td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: r.size_factor === 0.5 ? T.gold : T.text }}>
                    {r.size_factor != null ? `${Number(r.size_factor).toFixed(1)}x` : '—'}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: T.textMuted }}>{num(r.on_ret, 1)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: T.text }}>{num(r.entry_price)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: T.bear }}>{num(r.stop_level)}</td>
                  <td style={{ padding: '6px 10px' }}><Pill value={st} color={stColor} mono /></td>
                  <td style={{ padding: '6px 10px', color: T.textMuted }}>{r.exit_time ? fmtET(r.exit_time) : '—'}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 700,
                    color: r.pnl_bp == null ? T.textMuted : r.pnl_bp >= 0 ? T.bull : T.bear }}>
                    {r.pnl_bp != null ? num(r.pnl_bp, 1) : '—'}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 700,
                    color: r.pnl_sized_bp == null ? T.textMuted : r.pnl_sized_bp >= 0 ? T.bull : T.bear }}>
                    {r.pnl_sized_bp != null ? num(r.pnl_sized_bp, 1) : '—'}</td>
                  <td style={{ padding: '6px 10px', color: T.textMuted, fontSize: 10, maxWidth: 200,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.reason || ''}>{r.reason || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
    </div>
  );
}
